import React, { useRef, useState } from 'react';
import {
  Box,
  ClickAwayListener,
  MenuList,
  MenuItem,
  Popper,
  Paper,
  styled,
  Button,
  Collapse,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

export interface IntegrationCenterDropdownProps {
  workstreams: string[];
  partnerId: string;
  changeNavigation: (path: string) => void;
  mobile?: boolean;
}

export const IntegrationCenterDropdown = ({
  workstreams,
  partnerId,
  changeNavigation,
  mobile = false,
}: IntegrationCenterDropdownProps) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const handleClose = () => setOpen(false);
  const handleToggle = () => setOpen((prevOpen) => !prevOpen);
  const StyledMenuItem = styled(MenuItem)({
    margin: '0',
    textTransform: 'none',
  });

  const checkAPI = workstreams.includes('Billing_API');
  const checkADK = workstreams.includes('ADK_Integration');
  const checkCJVal = workstreams.includes('Customer_Journey');
  const checkPerks = workstreams.includes('Perks');

  const highlight = window.location.pathname.startsWith(`/app/partner/${partnerId}/`);

  if (mobile) {
    return (
      <>
        <MenuItem sx={{ m: 0 }} onClick={handleToggle} disableGutters>
          <Stack justifyContent="space-between" direction="row" width="100%">
            <span>Integration Center</span>
            <ExpandLessIcon
              sx={{
                letterSpacing: '.5px',
                transform: open ? undefined : 'rotate(0.5turn)',
                transition: 'transform .25s ease-out',
              }}
              fontSize="small"
            />
          </Stack>
        </MenuItem>
        <Collapse in={open}>
          <MenuList id="menu-list-dropdown" sx={{ p: 0 }}>
            {checkAPI && (
              <StyledMenuItem
                onClick={() => {
                  changeNavigation(`/app/partner/${partnerId}/api/config/provider-config`);
                  handleClose();
                }}
                sx={{ pl: 4, m: 0 }}
              >
                API
              </StyledMenuItem>
            )}
            {checkADK && (
              <StyledMenuItem
                onClick={() => {
                  changeNavigation(`/app/partner/${partnerId}/adk`);
                  handleClose();
                }}
                sx={{ pl: 4, m: 0 }}
              >
                ADK
              </StyledMenuItem>
            )}
            {checkPerks && (
              <StyledMenuItem
                onClick={() => {
                  changeNavigation(`/app/partner/${partnerId}/perks`);
                  handleClose();
                }}
                sx={{ pl: 4, m: 0 }}
              >
                Perks
              </StyledMenuItem>
            )}
            {checkCJVal && (
              <StyledMenuItem
                onClick={() => {
                  changeNavigation(`/app/partner/${partnerId}/customer-journey`);
                  handleClose();
                }}
                data-cy="customer-journey-opt"
                sx={{ pl: 4, m: 0 }}
              >
                Customer Journey
              </StyledMenuItem>
            )}
          </MenuList>
        </Collapse>
      </>
    );
  }

  return (
    <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', position: 'relative', float: 'right' }}>
      <Button
        onClick={handleToggle}
        color="inherit"
        ref={anchorRef}
        sx={{
          backgroundColor: highlight ? 'white' : '#1D2026',
          color: highlight ? '#1D2026' : 'white',
          py: 0.5,
          px: 1,
          textTransform: 'capitalize',
          letterSpacing: '.5px',
          fontSize: '1rem',
          '&:hover': {
            background: '#6C696A',
            color: 'white',
            textTransform: 'none',
          },
        }}
        aria-label={`${open ? 'Close' : 'Open'} integration menu`}
        aria-haspopup="true"
        aria-controls={open ? 'menu-list-dropdown' : undefined}
        size="large"
        data-cy="int-cent-ddown"
      >
        Integration Center
        {open ? (
          <ExpandLessIcon
            sx={{
              letterSpacing: '.5px',
              '&:hover': {
                background: '#6C696A',
                color: 'white',
              },
            }}
            fontSize="small"
          />
        ) : (
          <ExpandMoreIcon
            sx={{
              letterSpacing: '.5px',
              '&:hover': {
                background: '#6C696A',
                color: 'white',
              },
            }}
            fontSize="small"
          />
        )}
      </Button>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        disablePortal={true}
        placement="bottom-end"
        style={{ zIndex: 15, width: '250px' }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper>
            <MenuList id="menu-list-dropdown" style={{ padding: '0' }}>
              {checkAPI && (
                <StyledMenuItem
                  onClick={() => {
                    changeNavigation(`/app/partner/${partnerId}/api/config/provider-config`);
                    handleClose();
                  }}
                >
                  API
                </StyledMenuItem>
              )}
              {checkADK && (
                <StyledMenuItem
                  onClick={() => {
                    changeNavigation(`/app/partner/${partnerId}/adk`);
                    handleClose();
                  }}
                >
                  ADK
                </StyledMenuItem>
              )}
              {checkPerks && (
                <StyledMenuItem
                  onClick={() => {
                    changeNavigation(`/app/partner/${partnerId}/perks`);
                    handleClose();
                  }}
                >
                  Perks
                </StyledMenuItem>
              )}
              {checkCJVal && (
                <StyledMenuItem
                  onClick={() => {
                    changeNavigation(`/app/partner/${partnerId}/customer-journey`);
                    handleClose();
                  }}
                  data-cy="customer-journey-opt"
                >
                  Customer Journey
                </StyledMenuItem>
              )}
            </MenuList>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};
